import { supabase } from '../supabaseClient';
import { v4 as uuidv4 } from 'uuid';

// One token spans an autocomplete session and the details call that ends it,
// which Google bills as a single session rather than per keystroke.
let sessionToken = uuidv4();

function refreshSessionToken(): void {
  sessionToken = uuidv4();
}

export const MIN_PLACES_QUERY_LENGTH = 3;

/** Google reports most failures (billing, quota, a bad key) as HTTP 200 with
 *  a status field; only these two mean the search actually ran. */
const GOOGLE_OK_STATUSES = new Set(['OK', 'ZERO_RESULTS']);

export class PlacesLookupError extends Error {
  /** Google's status (REQUEST_DENIED, OVER_QUERY_LIMIT...) when it gave one. */
  readonly status: string | null;

  constructor(message: string, status: string | null = null) {
    super(message);
    this.name = 'PlacesLookupError';
    this.status = status;
  }
}

export interface PlacePrediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface PlaceDetails {
  placeId: string;
  name: string;
  formattedAddress: string;
  latitude: number | undefined;
  longitude: number | undefined;
  /** ISO 3166-1 alpha-2, upper case, when Google returned the country. */
  countryCode: string | null;
}

interface PlacesPayload {
  status?: string;
  error?: string;
  error_message?: string;
}

interface AddressComponent {
  short_name?: string;
  types?: string[];
}

/** Works with both shapes of failure: a non-2xx from the function (the SDK
 *  error carries the Response) and a 200 whose body holds Google's status. */
async function assertPlacesOk(data: unknown, error: unknown): Promise<void> {
  if (error) {
    const context = (error as { context?: Response | null }).context;
    let payload: PlacesPayload | null = null;
    try {
      payload = context ? await context.clone().json() as PlacesPayload : null;
    } catch {
      // Not JSON: fall back to the SDK's own message.
    }
    throw new PlacesLookupError(
      payload?.error ?? payload?.error_message ?? (error as Error).message ?? 'Address search failed',
      payload?.status ?? null,
    );
  }

  const status = (data as PlacesPayload | null)?.status;
  if (status && !GOOGLE_OK_STATUSES.has(status)) {
    throw new PlacesLookupError((data as PlacesPayload).error_message ?? status, status);
  }
}

function getCountryCode(components: AddressComponent[] | undefined): string | null {
  const code = components
    ?.find((component) => component.types?.includes('country'))
    ?.short_name
    ?.toUpperCase();
  return code && /^[A-Z]{2}$/.test(code) ? code : null;
}

export const googlePlacesService = {
  /** `country` limits results to that country (ISO alpha-2). */
  async getAutocompletePredictions(input: string, country?: string | null): Promise<PlacePrediction[]> {
    const query = input.trim();
    if (query.length < MIN_PLACES_QUERY_LENGTH) return [];

    const { data, error } = await supabase.functions.invoke('places_lookup', {
      body: {
        action: 'autocomplete',
        input: query,
        sessionToken,
        country: country ?? undefined,
      },
    });
    await assertPlacesOk(data, error);

    const predictions = (data as { predictions?: Array<{
      place_id: string;
      description: string;
      structured_formatting?: { main_text?: string; secondary_text?: string };
    }> })?.predictions ?? [];

    return predictions.map((p) => ({
      placeId: p.place_id,
      description: p.description,
      mainText: p.structured_formatting?.main_text || p.description,
      secondaryText: p.structured_formatting?.secondary_text ?? '',
    }));
  },

  async getPlaceDetails(placeId: string): Promise<PlaceDetails | null> {
    const { data, error } = await supabase.functions.invoke('places_lookup', {
      body: {
        action: 'details',
        placeId,
        sessionToken,
      },
    });

    // The details call closes the billing session, whatever its outcome.
    refreshSessionToken();
    await assertPlacesOk(data, error);

    const result = (data as { result?: {
      place_id: string;
      name?: string;
      formatted_address?: string;
      geometry?: { location?: { lat?: number; lng?: number } };
      address_components?: AddressComponent[];
    } })?.result;
    if (!result) return null;

    return {
      placeId: result.place_id,
      name: result.name ?? '',
      formattedAddress: result.formatted_address ?? '',
      latitude: result.geometry?.location?.lat,
      longitude: result.geometry?.location?.lng,
      countryCode: getCountryCode(result.address_components),
    };
  },
};
